import { z } from "zod";

import { VerificationResult } from "@/src/models/task-verification";

/**
 * Every action ops can take from a step card, the same for every customer. Which a card offers is
 * derived on the server (`resolveStepCardActions`), carried out in one place (`performStepCardAction`)
 * and recorded with the verdict as its `action`, so each one can be traced. Values are persisted:
 * never rename one.
 */
export enum StepCardActionType {
  /** "Approve": releases the approval the card holds, through its own checks, and records Correct. */
  APPROVE = "approve",
  /**
   * The single button Approve and Resume replaced. Never offered; still accepted from a page loaded
   * before they did, and done as whichever of them the card offers.
   */
  CONTINUE = "continue",
  /**
   * "Forward" on a decision card (`StepCardDefinition.decision`): closes the task as not relevant
   * and records the verdict derived from automation's answer.
   */
  FORWARD = "forward",
  /** "Proceed" on a decision card: releases the approval it holds and records the derived verdict. */
  PROCEED = "proceed",
  /** "Reject": declines the approval the card holds and blocks the task, recording Incorrect. */
  REJECT = "reject",
  /** "Resume": runs the `resumable` step the run is parked on again, which re-checks what it waited for. */
  RESUME = "resume",
  /** A form block's answer to the approval the card holds, for a type answered by a form. */
  SUBMIT = "submit",
  /** ✓ / ✗ (Correct / Incorrect) on automation's result. */
  VERDICT = "verdict",
}

export enum StepCardDecisionAsk {
  PROCEED_FORWARD = "proceed_forward",
  VERDICT = "verdict",
}

export enum StepCardDecisionForward {
  CLOSE_NOT_RELEVANT = "close_not_relevant",
  CONTINUE = "continue",
}

/** What a card lets the run go on through, chosen by the server from what the run waits on. */
export enum StepCardContinueVia {
  /** The pending approval the card holds: Approve / Reject, or Proceed / Forward on a decision card. */
  APPROVAL = "approval",
  /** Runs the `resumable` step the run is parked on again, which re-checks what it waited for. */
  RESUME = "resume",
}

/**
 * One action submitted from a card. `revision` is the card's revision on the page (when any row behind
 * it last changed), and `targetId` names the row or approval the action acts on, so a click made
 * against a page that has since moved on is refused rather than applied to something else.
 */
export const stepCardActionRequestSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(StepCardActionType.APPROVE), targetId: z.uuid(), revision: z.string() }),
  z.object({ type: z.literal(StepCardActionType.CONTINUE), targetId: z.uuid(), revision: z.string() }),
  z.object({ type: z.literal(StepCardActionType.REJECT), targetId: z.uuid(), revision: z.string() }),
  z.object({ type: z.literal(StepCardActionType.RESUME), targetId: z.uuid(), revision: z.string() }),
  z.object({ type: z.literal(StepCardActionType.PROCEED), targetId: z.uuid(), revision: z.string() }),
  z.object({ type: z.literal(StepCardActionType.FORWARD), targetId: z.uuid(), revision: z.string() }),
  z.object({
    type: z.literal(StepCardActionType.SUBMIT),
    targetId: z.uuid(),
    revision: z.string(),
    answer: z.unknown(),
  }),
  z.object({
    type: z.literal(StepCardActionType.VERDICT),
    verdict: z.enum(VerificationResult),
    revision: z.string(),
  }),
]);

export type StepCardActionRequest = z.infer<typeof stepCardActionRequestSchema>;

export const stepCardActionResponseSchema = z.object({
  success: z.literal(true),
  /** Whether the action restarted the workflow. */
  resumed: z.boolean(),
});

export type StepCardActionResponse = z.infer<typeof stepCardActionResponseSchema>;
